import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { Bed, DoorOpen, Building2, Wifi, Refrigerator, Coffee, Tv } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { ReviewSnippetStrip } from "../components/GoogleReviews";
import { PersonaChips } from "../components/PersonaChips";
import { FaqAccordion, buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { Picture, findPhoto } from "../lib/photos";
import { Lightbox } from "../components/Lightbox";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RoomDetail() {
  const { slug } = useParams();
  const [room, setRoom] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    let alive = true;
    setNotFound(false);
    setRoom(null);
    (async () => {
      try {
        const [rr, pp] = await Promise.all([
          axios.get(`${API}/rooms/${slug}`).then((r) => r.data).catch(() => null),
          axios.get(`${API}/photos`).then((r) => r.data),
        ]);
        if (!alive) return;
        if (!rr) {
          setNotFound(true);
          return;
        }
        setRoom(rr);
        setPhotos(pp.photos || []);
      } catch (e) {
        console.error("room detail fetch fail", e);
        setNotFound(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const hero = useMemo(() => (room && photos.length ? findPhoto(photos, room.photo_slug) : null), [room, photos]);
  const gallery = useMemo(
    () => (room ? (room.photo_slugs || []).map((s) => findPhoto(photos, s)).filter(Boolean) : []),
    [room, photos],
  );

  const canonical = room ? `https://westbridgehotels.com/rooms/${room.slug}` : undefined;
  useSeo({
    title: room?.meta_title || "Room — Westbridge Inn & Suites",
    description: room?.meta_description || "",
    canonical,
    image: hero ? `${window.location.origin}${hero.jpg}` : undefined,
    jsonLd: room
      ? [
          buildBreadcrumbLd([
            { label: "Rooms", to: "/rooms" },
            { label: room.name, to: `/rooms/${room.slug}` },
          ]),
          buildFaqLd(room.faqs),
          {
            "@context": "https://schema.org",
            "@type": "HotelRoom",
            name: room.name,
            description: room.short,
            occupancy: {
              "@type": "QuantitativeValue",
              maxValue: room.sleeps,
            },
            bed: room.beds,
            amenityFeature: (room.features || []).map((f) => ({
              "@type": "LocationFeatureSpecification",
              name: f,
              value: true,
            })),
            image: hero ? [`${window.location.origin}${hero.jpg}`] : undefined,
          },
        ]
      : [],
  });

  if (notFound) {
    return (
      <main id="main" className="pt-40 pb-24 min-h-[60vh] bg-cream">
        <div className="container-app text-center">
          <p className="eyebrow text-brass mb-3">404</p>
          <h1 className="font-display text-4xl md:text-5xl text-forest-900 mb-4">Room not found</h1>
          <Link to="/rooms" className="btn-ghost-dark">See all rooms</Link>
        </div>
      </main>
    );
  }

  if (!room) {
    return (
      <main id="main" className="pt-40 pb-24 min-h-[240vh] bg-cream" data-testid="room-loading">
        <div className="container-app">
          <div className="w-full h-96 bg-tan-200 animate-pulse" />
        </div>
      </main>
    );
  }

  return (
    <main id="main" data-testid="room-detail">
      <PageHero
        eyebrow={room.corridor === "interior" ? "Interior corridor" : "Exterior access"}
        title={room.name}
        intro={room.short}
        photo={hero}
        breadcrumbs={[
          { label: "Rooms", to: "/rooms" },
          { label: room.name, to: `/rooms/${room.slug}` },
        ]}
        testId="room-hero"
      />

      {/* Overview + facts */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-7 max-w-2xl">
            <p className="eyebrow mb-4">Overview</p>
            <p className="text-ink/85 text-lg leading-relaxed">{room.intro}</p>
            <div className="mt-8">
              <p className="eyebrow mb-3 text-forest-800">Best for</p>
              <PersonaChips items={room.best_for} />
            </div>
          </div>
          <aside className="md:col-span-5 bg-cream-100 border border-tan p-6 md:p-8">
            <p className="eyebrow mb-4">At a glance</p>
            <div className="space-y-4 text-sm">
              <Row icon={Bed} label="Sleeps" value={`${room.sleeps} · ${room.beds}`} />
              <Row
                icon={room.corridor === "interior" ? Building2 : DoorOpen}
                label="Access"
                value={
                  room.corridor === "interior"
                    ? "Interior corridor · Lobby entry"
                    : "Exterior access · Park at your door"
                }
              />
              <Row icon={Refrigerator} label="Fridge & microwave" value="Yes · standard" />
              <Row icon={Coffee} label="Coffee maker" value="Yes · standard" />
              <Row icon={Tv} label="TV" value='55" 4K LED · cable channels' />
              <Row icon={Wifi} label="Wi-Fi" value="Free · high-speed" />
            </div>
            <p className="mt-6 text-xs text-ink/60 leading-relaxed">
              {room.price_hint}
            </p>
          </aside>
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="room-gallery">
          <div className="container-app relative">
            <div className="mb-10">
              <p className="eyebrow mb-3">Photos</p>
              <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight">
                Real photos of the room and property.
              </h2>
            </div>
            <div className="grid grid-cols-6 gap-3 md:gap-4">
              {gallery.map((p, i) => {
                const spans = [
                  "col-span-6 md:col-span-4 aspect-[16/10]",
                  "col-span-3 md:col-span-2 aspect-[4/5]",
                  "col-span-3 md:col-span-2 aspect-square",
                  "col-span-6 md:col-span-4 aspect-[4/3]",
                ];
                return (
                  <button
                    key={p.slug}
                    onClick={() => setLightboxIdx(i)}
                    className={`group relative overflow-hidden bg-forest-800 ${spans[i % spans.length]}`}
                    data-testid={`room-gallery-tile-${i}`}
                    aria-label={`Open lightbox: ${p.alt}`}
                  >
                    <Picture
                      photo={p}
                      className="absolute inset-0 w-full h-full"
                      imgClassName="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]"
                      sizes="(min-width: 768px) 40vw, 100vw"
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <Lightbox
            photos={gallery}
            index={lightboxIdx}
            onClose={() => setLightboxIdx(-1)}
            onPrev={() => setLightboxIdx((i) => (i - 1 + gallery.length) % gallery.length)}
            onNext={() => setLightboxIdx((i) => (i + 1) % gallery.length)}
          />
        </section>
      )}

      {/* Details grid */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-2 gap-10 md:gap-14">
          <div>
            <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">Bedroom</h3>
            <p className="text-ink/85 leading-relaxed">{room.bedroom_notes}</p>
          </div>
          <div>
            <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">Bathroom</h3>
            <p className="text-ink/85 leading-relaxed">{room.bathroom_notes}</p>
          </div>
          <div>
            <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">Workspace</h3>
            <p className="text-ink/85 leading-relaxed">{room.workspace_notes}</p>
          </div>
          <div>
            <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">Parking &amp; access</h3>
            <p className="text-ink/85 leading-relaxed">{room.parking_notes}</p>
          </div>
        </div>

        {/* Features list */}
        <div className="container-app mt-14">
          <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-6">Features</h3>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-ink/85">
            {room.features?.map((f) => (
              <li key={f} className="flex gap-2">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brass shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQs */}
      {room.faqs?.length > 0 && (
        <section className="bg-cream-100 py-16 md:py-24 paper-grain" data-testid="room-faq">
          <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14">
            <div className="md:col-span-4">
              <p className="eyebrow mb-3">Common questions</p>
              <h2 className="font-display font-light text-2xl md:text-4xl text-forest-900 tracking-tight leading-tight">
                About the {room.name}.
              </h2>
            </div>
            <div className="md:col-span-8 relative">
              <FaqAccordion items={room.faqs} testIdPrefix="room-faq" />
            </div>
          </div>
        </section>
      )}

      <ReviewSnippetStrip ids={["royals-fan", "julius-t"]} variant="light" testId="room-review-strip" />

      <CtaBlock
        eyebrow="Book this room"
        title={`Reserve the ${room.name}.`}
        body="Book directly on our reservation page or call the front desk if you'd rather talk to a real person about crew blocks, extra beds, or accessibility."
      />
    </main>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-brass mt-1 flex-none" strokeWidth={1.5} aria-hidden="true" />
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-ink/60">{label}</p>
        <p className="text-forest-900">{value}</p>
      </div>
    </div>
  );
}
